import { useState, useMemo } from 'react'
import type { SessionStats, SoberStreak } from '@/types'
import { toCompactDuration, formatOrdinalDate } from '@/utils/time'

interface InsightPanelsProps {
  historicalSessions: SessionStats[]
  allSessions: SessionStats[]
  soberStreaks: SoberStreak[]
  tick: number
}

function sessionTitle(index: number): string {
  if (index === 0) return 'Last Session'
  if (index === 1) return '2nd Last Session'
  if (index === 2) return '3rd Last Session'
  return `${index + 1}th Last Session`
}

interface WorstCase {
  label: string
  value: string
  drinkCount: number
  peakBac: number
  date: string
}

export default function InsightPanels({ historicalSessions, allSessions, soberStreaks, tick: _tick }: InsightPanelsProps) {
  const [sessionIndex, setSessionIndex] = useState(0)
  const [streakIndex, setStreakIndex] = useState(0)
  const [worstIndex, setWorstIndex] = useState(0)

  const currentSession = historicalSessions[sessionIndex] ?? null
  const currentStreak = soberStreaks[streakIndex] ?? null

  const cycleSession = () => {
    setSessionIndex((prev) => (prev + 1) % historicalSessions.length)
  }

  const cycleStreak = () => {
    setStreakIndex((prev) => (prev + 1) % soberStreaks.length)
  }

  // Compute session duration
  let sessionDuration = ''
  if (currentSession && currentSession.drinks.length > 0) {
    const first = new Date(currentSession.drinks[0].finished_at).getTime()
    const last = new Date(currentSession.drinks[currentSession.drinks.length - 1].finished_at).getTime()
    const durationSecs = Math.max(0, (last - first) / 1000)
    sessionDuration = toCompactDuration(durationSecs)
  }

  // Compute "time since" for sober streak
  let streakTimeSince = ''
  if (currentStreak) {
    const now = Date.now()
    const startMs = new Date(currentStreak.startedAt).getTime()
    const secsSince = Math.max(0, (now - startMs) / 1000)
    streakTimeSince = toCompactDuration(secsSince) + ' ago'
  }

  // Compute worst cases
  const worstCases = useMemo((): WorstCase[] => {
    if (allSessions.length === 0) return []

    const cases: WorstCase[] = []

    // Worst BAC session
    const worstBacSession = allSessions.reduce((worst, s) =>
      s.peakBac > worst.peakBac ? s : worst
    )
    cases.push({
      label: 'Worst BAC',
      value: `${worstBacSession.peakBac.toFixed(3)}%`,
      drinkCount: worstBacSession.drinkCount,
      peakBac: worstBacSession.peakBac,
      date: worstBacSession.drinks[0]?.finished_at ?? '',
    })

    // Most drinks session
    const mostDrinksSession = allSessions.reduce((worst, s) =>
      s.drinkCount > worst.drinkCount ? s : worst
    )
    // Only add if it's a different session
    if (mostDrinksSession !== worstBacSession) {
      cases.push({
        label: 'Most Drinks',
        value: `${mostDrinksSession.drinkCount} drinks`,
        drinkCount: mostDrinksSession.drinkCount,
        peakBac: mostDrinksSession.peakBac,
        date: mostDrinksSession.drinks[0]?.finished_at ?? '',
      })
    }

    return cases
  }, [allSessions])

  const currentWorst = worstCases[worstIndex] ?? null

  const cycleWorst = () => {
    setWorstIndex((prev) => (prev + 1) % worstCases.length)
  }

  return (
    <div className="flex items-stretch gap-4 overflow-x-auto snap-x snap-mandatory md:grid md:grid-cols-3 md:overflow-visible">
      {/* Last Session Panel */}
      <div className="card min-w-[200px] flex-shrink-0 snap-start md:min-w-0 md:flex-shrink flex flex-col">
        <div className="flex items-center justify-between mb-2 h-11">
          <p className="text-sm text-[var(--text-secondary)]">{sessionTitle(sessionIndex)}</p>
          {historicalSessions.length > 1 && (
            <button
              onClick={cycleSession}
              className="p-2 rounded hover:bg-[var(--bg-card)] transition-colors"
              aria-label="Next session"
            >
              <svg className="w-7 h-7" fill="#a855f7" viewBox="0 0 24 24">
                <circle cx="5" cy="12" r="2.5" />
                <circle cx="12" cy="12" r="2.5" />
                <circle cx="19" cy="12" r="2.5" />
              </svg>
            </button>
          )}
        </div>
        {currentSession ? (
          <>
            <p className="text-3xl font-bold font-mono text-[var(--text-primary)] mb-auto">
              {currentSession.peakBac.toFixed(3)}%
            </p>
            <div className="flex items-center justify-between mt-3 text-sm text-[var(--text-secondary)]">
              <span>{currentSession.drinkCount} drink{currentSession.drinkCount !== 1 ? 's' : ''}</span>
              <span>{sessionDuration}</span>
            </div>
          </>
        ) : (
          <p className="text-sm text-[var(--text-secondary)] opacity-60 mt-2">No previous sessions</p>
        )}
      </div>

      {/* Best Sobriety Panel */}
      <div className="card min-w-[200px] flex-shrink-0 snap-start md:min-w-0 md:flex-shrink flex flex-col">
        <div className="flex items-center justify-between mb-2 h-11">
          <p className="text-sm text-[var(--text-secondary)]">
            {streakIndex === 0 ? 'Best' : streakIndex === 1 ? '2nd Best' : streakIndex === 2 ? '3rd Best' : `${streakIndex + 1}th Best`} Sobriety
          </p>
          {soberStreaks.length > 1 && (
            <button
              onClick={cycleStreak}
              className="p-2 rounded hover:bg-[var(--bg-card)] transition-colors"
              aria-label="Next streak"
            >
              <svg className="w-7 h-7" fill="#a855f7" viewBox="0 0 24 24">
                <circle cx="5" cy="12" r="2.5" />
                <circle cx="12" cy="12" r="2.5" />
                <circle cx="19" cy="12" r="2.5" />
              </svg>
            </button>
          )}
        </div>
        {currentStreak ? (
          <>
            <p className="text-3xl font-bold font-mono text-[var(--text-primary)] mb-auto">
              {toCompactDuration(currentStreak.durationSeconds)}
            </p>
            <div className="flex items-center justify-between mt-3 text-sm text-[var(--text-secondary)]">
              <span>{formatOrdinalDate(currentStreak.startedAt)}</span>
              <span>{streakTimeSince}</span>
            </div>
          </>
        ) : (
          <p className="text-sm text-[var(--text-secondary)] opacity-60 mt-2">Not enough data</p>
        )}
      </div>

      {/* Worst Cases Panel */}
      <div className="card min-w-[200px] flex-shrink-0 snap-start md:min-w-0 md:flex-shrink flex flex-col">
        <div className="flex items-center justify-between mb-2 h-11">
          <p className="text-sm text-[var(--text-secondary)]">
            {currentWorst?.label ?? 'Worst Cases'}
          </p>
          {worstCases.length > 1 && (
            <button
              onClick={cycleWorst}
              className="p-2 rounded hover:bg-[var(--bg-card)] transition-colors"
              aria-label="Next worst case"
            >
              <svg className="w-7 h-7" fill="#a855f7" viewBox="0 0 24 24">
                <circle cx="5" cy="12" r="2.5" />
                <circle cx="12" cy="12" r="2.5" />
                <circle cx="19" cy="12" r="2.5" />
              </svg>
            </button>
          )}
        </div>
        {currentWorst ? (
          <>
            <p className="text-3xl font-bold font-mono text-[var(--bac-danger)] mb-auto">
              {currentWorst.value}
            </p>
            <div className="flex items-center justify-between mt-3 text-sm text-[var(--text-secondary)]">
              <span>
                {currentWorst.label === 'Worst BAC'
                  ? `${currentWorst.drinkCount} drink${currentWorst.drinkCount !== 1 ? 's' : ''}`
                  : `${currentWorst.peakBac.toFixed(3)}%`
                }
              </span>
              <span>{formatOrdinalDate(currentWorst.date)}</span>
            </div>
          </>
        ) : (
          <p className="text-sm text-[var(--text-secondary)] opacity-60 mt-2">Not enough data</p>
        )}
      </div>
    </div>
  )
}
